import { Box, Tag, Text } from '@lego/klik-ui';
import { useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@/components/common/table/appTable';
import { TableStoreProvider } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import type { ImageIndexChild, ImageTagApplication } from '@/hooks/repository';
import { severityColorMap } from '../vulnerabilities/SeverityDetails';
import { ScanFindingsTable } from './ScanFindingsTable';

type Props = {
  indexChildren: ImageIndexChild[];
  applications: ImageTagApplication[];
  workspaceId: number;
  repositoryId: number;
  imageTag: string;
};

const columnHelper = createColumnHelper<ImageIndexChild>();

const shortenDigest = (digest: string): string => {
  const parts = digest.split(':');
  if (parts.length === 2) {
    return `${parts[0]}:${parts[1].substring(0, 12)}`;
  }
  return digest.substring(0, 19);
};

export const IndexChildrenTable = ({
  indexChildren,
  applications,
  workspaceId,
  repositoryId,
  imageTag,
}: Props) => {
  const [selectedDigest, setSelectedDigest] = useState<string>(indexChildren[0]?.digest);

  useEffect(() => {
    setSelectedDigest(indexChildren[0]?.digest);
  }, [indexChildren]);

  const selectedChild = indexChildren.find((c) => c.digest === selectedDigest);

  const tableColumns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('architecture', {
          header: 'Architecture',
        }),
        columnHelper.accessor('os', {
          header: 'OS',
        }),
        columnHelper.accessor('digest', {
          header: 'Digest',
          cell: (info) => (
            <Text fontFamily="mono" fontSize="sm" title={info.getValue() as string}>
              {shortenDigest(info.getValue() as string)}
            </Text>
          ),
        }),
        columnHelper.accessor((row) => row.scanResults?.scanStatus ?? 'N/A', {
          header: 'Scan Status',
          id: 'scanStatus',
        }),
        columnHelper.accessor((row) => row.scanResults?.findingSeverityCounts, {
          header: 'Findings',
          id: 'findings',
          cell: (info) => {
            const counts = info.row.original.scanResults?.findingSeverityCounts;
            if (!counts) return <Text>N/A</Text>;

            const entries = Object.entries(counts).filter(([, v]) => v && v > 0);
            if (entries.length === 0) return <Text>None</Text>;

            return (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {entries.map(([severity, count]) => {
                  const normalizedSeverity = severity.toUpperCase();
                  return (
                    <Tag
                      colorScheme={severityColorMap[normalizedSeverity] ?? 'slate'}
                      key={severity}
                      size="sm"
                    >
                      {normalizedSeverity}: {count}
                    </Tag>
                  );
                })}
              </Box>
            );
          },
        }),
      ]),
    []
  );

  const selectedDataIndex = indexChildren.findIndex((c) => c.digest === selectedDigest);

  return (
    <>
      <Box
        css={{
          '& tbody tr': { transition: 'background-color 0.2s' },
          ...(selectedDataIndex >= 0 && {
            [`& tbody tr:nth-of-type(${selectedDataIndex + 1})`]: {
              backgroundColor: 'var(--klik-colors-light-blue-50, #eff6ff)',
            },
          }),
        }}
      >
        <TableStoreProvider initialSorting={[]} name="IndexChildren">
          <Table
            columns={tableColumns}
            data={indexChildren}
            onRowClick={(rowData) => setSelectedDigest(rowData.digest)}
            title={`Architecture-Specific Images (${indexChildren.length})`}
          />
        </TableStoreProvider>
      </Box>
      {selectedChild && (
        <ScanFindingsTable
          applications={applications}
          description={
            <Text color="gray.500" fontFamily="mono" fontSize="sm">
              {selectedChild.digest}
            </Text>
          }
          imageTag={imageTag}
          key={selectedChild.digest}
          repositoryId={repositoryId}
          scanResults={selectedChild.scanResults}
          title={`Scan Findings - ${selectedChild.os}/${selectedChild.architecture}`}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};
